import type { FC } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { EditSolidIcon } from '@dhl-ui/icons'
import { Body2, ListItem, ListItemText } from '@dhl-ui/components'

import type { LocationStateWithReferrer } from '~/types/locationState.js'
import { links } from '~/links.js'
import { useGetDeliveryPreferenceForAddress } from '../../preferences/hooks/useGetDeliveryPreferenceForAddress.js'
import { DeliveryPreferenceDescription } from '../../preferences/components/DeliveryPreferenceDescription.js'

export type Params = {
  addressId: string
}

type Props = {
  isAddressMutated?: boolean
}

export const AddressFormDeliveryPreference: FC<Props> = ({ isAddressMutated }) => {
  const { t } = useTranslation()

  const location = useLocation<LocationStateWithReferrer>()
  const navigate = useNavigate()
  const { addressId } = useParams<Params>()
  const { data: preference } = useGetDeliveryPreferenceForAddress(addressId)

  const handleShippingPreferenceClick = () => {
    const { pathname, state } = location
    const target = links.deliveryPreferenceDetailsLink({ addressId })

    navigate(target, {
      state: {
        ...state,
        referrer: pathname,
      },
    })
  }

  if (!addressId) {
    return null
  }

  return (
    <ListItem
      isButton
      hasDivider={false}
      alignItems="center"
      data-test="shipping-preferences"
      isDisabled={isAddressMutated}
      slotProps={{
        secondaryIcon: <EditSolidIcon />,
      }}
      onClick={handleShippingPreferenceClick}
    >
      <ListItemText
        emphasis
        data-test="shipping-preferences-text"
        primary={t('portal.profile.addressForm.editShippingPreferences')}
        secondary={
          <Body2 data-test="active-shipping-preference" translate="no" component="span">
            {preference?.preference && <DeliveryPreferenceDescription preference={preference?.preference} />}
          </Body2>
        }
      />
    </ListItem>
  )
}
